






















































import {
  computed, defineComponent, onMounted, onUnmounted, ref, toRefs, watch,
} from '@vue/composition-api';
import { PropType } from 'vue';
import {
  CustomInputValidationState,
  useNumberFormat,
  useNumberInputEvents,
  useNumberParsing,
  useNumberValidation,
} from './BaseNumberInputElementFeatures';

/**
 * New number input with locale support.
 */
export default defineComponent({
  emit: [
    'change',
  ],
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    /**
     * the value bind by v-model
     */
    value: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     * step size for increment and decrement
     */
    step: {
      type: Number,
      default: 1,
      required: false,
    },
    /**
     * disabled state
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * controls whether hint should be displayed or not
     */
    showHint: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * valid state coming from caller component
     * type: CustomInputValidationState
     */
    validState: {
      type: Object as PropType<CustomInputValidationState>,
      required: false,
      default: null,
    },
    /**
     * input type: number, output type: {customValid: Boolean, customErrorMessage: String}
     */
    validator: {
      type: Function as PropType<(number: number) => CustomInputValidationState>,
      required: false,
      default: () => ({ customValid: true }),
    },
    /**
     * title for <label>
     */
    title: {
      type: String,
      required: false,
      default: undefined,
    },
    /**
     * description below the input
     */
    description: {
      type: String,
      required: false,
      default: undefined,
    },
    /**
     * locale for formatting
     */
    locale: {
      type: String,
      default: 'en-US',
    },
    /**
     * affects thousand grouping for locale formattting
     */
    useGrouping: {
      type: Boolean,
      default: true,
    },
    /**
     * min fraction after decimal sign
     */
    minDecimal: {
      type: Number,
      default: 0,
      validator: (minDecimal: number) => minDecimal >= 0,
    },
    /**
     * max fraction after decimal sign
     */
    maxDecimal: {
      type: Number,
      default: 0,
      validator: (maxDecimal: number) => maxDecimal >= 0,
    },
    /**
     * min allowed value
     */
    min: {
      type: Number,
      default: null,
    },
    /**
     * max allowed value
     */
    max: {
      type: Number,
      default: null,
    },
    preventEnter: {
      type: Boolean,
      default: false,
    },
    elementName: {
      type: String,
      default: null,
    },
    textAlign: {
      type: String,
      default: 'left',
      validator: (val: string) => ['left', 'center', 'right'].some((alignment) => alignment === val),
    },
    floatStyle: {
      type: Boolean,
      default: false,
    },
    inputClasses: {
      type: String,
      default: null,
    },
  },
  name: 'BaseNumberInputElement',
  components: {},
  setup(props, { emit }) {
    const { value } = toRefs(props);
    const internalValue = ref(value.value);

    const { formatValue, formattedValue } = useNumberFormat(props);

    const { decimalChar, parseValue } = useNumberParsing(props);

    const { valid, errorMessage, validate } = useNumberValidation(internalValue, props);

    const {
      isInputFocused, onInputFocus, onInputBlur, onInputKeyPress,
    } = useNumberInputEvents(
      internalValue,
      formatValue,
      decimalChar,
      props,
      emit,
      parseValue,
    );

    const id = computed(() => Math.random().toString(36).substring(7));

    if (props.min != null && props.max != null && props.min > props.max) {
      throw new Error('min property is larger than max property!');
    }

    if (
      props.minDecimal != null
      && props.maxDecimal != null
      && props.minDecimal > props.maxDecimal
    ) {
      throw new Error('minDecimal property is larger than maxDecimal property!');
    }

    const propsValueUnwatch = watch(() => props.value, () => {
      internalValue.value = props.value;
      validate();
    });

    onMounted(() => {
      if (internalValue.value != null) {
        validate();
      }
    });

    onUnmounted(() => {
      propsValueUnwatch();
    });

    return {
      id,
      internalValue,
      formattedValue,
      isInputFocused,
      onInputFocus,
      onInputBlur,
      onInputKeyPress,
      valid,
      errorMessage,
    };
  },
});
